import React, { useEffect, useCallback, useReducer, useRef } from 'react';
import { ActionRegistry } from '@packages/table-builder';
import type { ActionIntent, ActionExecutionContext } from '@packages/table-builder';
import { ComposeModal } from './ComposeModal';

// ── Imperative modal-close API ────────────────────────────────────
// Action classes dispatch these events to close modals from outside React.

interface ModalCloseDetail {
  actionId?: string;
  top?: boolean;
}

/** Close all open modals in the intent handler stack. */
export function closeAllModals(): void {
  window.dispatchEvent(new Event('ui:close-all-modals'));
}

/** Close a specific modal by its builder/loader action ID. */
export function closeModal(actionId: string): void {
  window.dispatchEvent(new CustomEvent<ModalCloseDetail>('ui:close-modal', { detail: { actionId } }));
}

/** Close only the topmost open modal in the stack. */
export function closeTopModal(): void {
  window.dispatchEvent(new CustomEvent<ModalCloseDetail>('ui:close-modal', { detail: { top: true } }));
}

type ModalEntry = {
  Component: React.ComponentType<any>;
  context?: ActionExecutionContext;
  payload?: Record<string, unknown>;
  key: string;
  actionId: string;
  isOpen: boolean;
  closing: boolean;
  isComposeModal?: boolean;
};

const modalLoaders: Record<string, () => Promise<{ default: React.ComponentType<any> }>> = {
  // ── Participant ─────────────────────────────────────────────────
  // Individual VIEW/ADD: OPEN_COMPOSE_MODAL → BUILD_PARTICIPANT_FORM
  // Individual SUMMARY: OPEN_COMPOSE_MODAL → BUILD_PARTICIPANT_VIEW
  // Entity SUMMARY/VIEW/EDIT: OPEN_MODAL → loaders below
  SUMMARY_PARTICIPANT: () => import('@apps/customer/domains/participant/SummaryParticipant'),
  VIEW_PARTICIPANT: () => import('@apps/customer/domains/participant/ViewParticipant'),
  EDIT_PARTICIPANT: () => import('@apps/customer/domains/participant/EditParticipant'),
  TERMINATE_PARTICIPANT: () => import('@apps/customer/domains/participant/TerminateParticipant'),
  VIEW_TERMINATION_PARTICIPANT: () => import('@apps/customer/domains/participant/TerminateParticipant'),
  VIEW_AUDIT_LOG_DETAIL_PARTICIPANT: () => import('@apps/customer/domains/participant/AuditLogView'),

  // ── Participant Approval ────────────────────────────────────────
  VIEW_PARTICIPANT_APPROVAL: () =>
    import('@apps/customer/domains/participant-approval/ViewParticipantApproval'),

  // ── Participant Broadcast ───────────────────────────────────────

  // ── Grant Documents & Terms & Conditions ─────────────────────────
  // Grant Agreement → BUILD_GRANT_AGREEMENT_FORM
  // Grant Notice    → BUILD_GRANT_NOTICE_FORM
  // T&C Document   → BUILD_TERMS_CONDITIONS_DOC_FORM
  // T&C Text       → BUILD_TERMS_CONDITIONS_TEXT_FORM
  // All use OPEN_COMPOSE_MODAL (no loader needed)

  // ── Securities ──────────────────────────────────────────────────
  VIEW_SECURITIES: () => import('@apps/customer/domains/securities/ViewSecurity'),
  EDIT_SECURITIES: () => import('@apps/customer/domains/securities/EditSecurity'),
  ADD_SECURITIES: () => import('@apps/customer/domains/securities/EditSecurity'),

  // ── Warrants ────────────────────────────────────────────────────
  VIEW_WARRANTS: () => import('@apps/customer/domains/warrants/ViewWarrant'),

  // ── Convertibles ────────────────────────────────────────────────
  VIEW_CONVERTIBLE: () => import('@apps/customer/domains/convertible/ViewConvertible'),

  // ── Charter & Stock Classes ───────────────────────────────────
  FILE_INITIAL_CHARTER: () => import('@apps/customer/domains/charter-stock-class/FileInitialCharter'),
  AMEND_CHARTER: () => import('@apps/customer/domains/charter-stock-class/AmendCharter'),
  EDIT_CHARTER: () => import('@apps/customer/domains/charter-stock-class/EditCharter'),
  IMPORT_CHARTER: () => import('@apps/customer/domains/charter-stock-class/ImportCharter'),

  // ── Valuations ──────────────────────────────────────────────────
  VIEW_VALUATION: () => import('@apps/customer/domains/valuation/ViewValuation'),
  EDIT_VALUATION: () => import('@apps/customer/domains/valuation/EditValuation'),
  ADD_VALUATION: () => import('@apps/customer/domains/valuation/EditValuation'),

  // ── PSU Awards ──────────────────────────────────────────────
  SUMMARY_PSU_AWARDS: () => import('@apps/customer/domains/psu-awards/ViewPsuAwardModal'),
  VIEW_COMMENTS_PSU_AWARDS: () => import('@apps/customer/domains/psu-awards/ViewCommentsPsuAwardModal'),

  // ── RSU Awards ──────────────────────────────────────────────
  SUMMARY_RSU_AWARDS: () => import('@apps/customer/domains/rsu-awards/ViewRsuAwardModal'),
  VIEW_COMMENTS_RSU_AWARDS: () => import('@apps/customer/domains/rsu-awards/ViewCommentsRsuAwardModal'),

  // ── User-Defined Fields ─────────────────────────────────────
  ADD_USER_DEFINED_FIELDS: () =>
    import('@apps/customer/domains/user-defined-fields/UserDefinedFieldModal'),
  EDIT_USER_DEFINED_FIELDS: () =>
    import('@apps/customer/domains/user-defined-fields/UserDefinedFieldModal'),

  // ── Vendor Terms & Conditions ───────────────────────────────
  ADD_VENDOR_TERMS_CONDITIONS: () =>
    import('@apps/customer/domains/vendor-terms-conditions/VendorTermsConditionsModal'),
  VIEW_VENDOR_TERMS_CONDITIONS: () =>
    import('@apps/customer/domains/vendor-terms-conditions/VendorTermsConditionsModal'),
  EDIT_VENDOR_TERMS_CONDITIONS: () =>
    import('@apps/customer/domains/vendor-terms-conditions/VendorTermsConditionsModal'),

  // ── Grant Management ────────────────────────────────────────────
  VIEW_GRANT_MANAGEMENT: () => import('@apps/customer/domains/grant-management/GrantManagementModal'),
  EDIT_GRANT_MANAGEMENT: () => import('@apps/customer/domains/grant-management/GrantManagementModal'),
  ADD_GRANT_MANAGEMENT: () => import('@apps/customer/domains/grant-management/GrantManagementModal'),

  // ── Grant Acceptance Template ───────────────────────────────────
  ADD_GRANT_ACCEPTANCE_TEMPLATE: () =>
    import('@apps/customer/domains/grant-acceptance-template/GrantAcceptanceTemplateModal'),
  ADD_VERSION_GRANT_ACCEPTANCE_TEMPLATE: () =>
    import('@apps/customer/domains/grant-acceptance-template/GrantAcceptanceTemplateModal'),
  EDIT_CONTENT_GRANT_ACCEPTANCE_TEMPLATE: () =>
    import('@apps/customer/domains/grant-acceptance-template/GrantAcceptanceTemplateModal'),
  COPY_CONTENT_GRANT_ACCEPTANCE_TEMPLATE: () =>
    import('@apps/customer/domains/grant-acceptance-template/GrantAcceptanceTemplateModal'),

  // ── Plan Approval ───────────────────────────────────────────────
  VIEW_PLAN_APPROVAL: () =>
    import('@apps/customer/domains/plan-approval/PlanManagementApprovalModel'),
  VIEW_COMMENTS_PLAN_APPROVAL: () =>
    import('@apps/customer/domains/plan-approval/ViewCommentsPlanApprovalModal'),

  // ── Vesting Templates ───────────────────────────────────────────
  // VIEW/EDIT/ADD use OPEN_COMPOSE_MODAL → BUILD_VESTING_TEMPLATE_FORM (no loader needed)

  // ── Option Awards ─────────────────────────────────────────────
  SUMMARY_OPTION_AWARDS: () => import('@apps/customer/domains/option-awards/ViewOptionAward'),
  VIEW_COMMENTS_OPTION_AWARDS: () =>
    import('@apps/customer/domains/option-awards/ViewCommentsOptionAwardModal'),

  // ── Option Award Approval ───────────────────────────────────────
  VIEW_OPTION_AWARD_APPROVAL: () =>
    import('@apps/customer/domains/option-award-approval/ViewOptionAwardApproval'),
  VIEW_COMMENTS_OPTION_AWARD_APPROVAL: () =>
    import('@apps/customer/domains/option-award-approval/ViewCommentsOptionAwardApprovalModal'),

  // ── RSU Award Approval ────────────────────────────────────────
  VIEW_RSU_AWARD_APPROVAL: () => import('@apps/customer/domains/rsu-award-approval/ViewRsuAwardApproval'),
  VIEW_COMMENTS_RSU_AWARD_APPROVAL: () =>
    import('@apps/customer/domains/rsu-award-approval/ViewCommentsRsuAwardApproval'),
  BULK_PROGRESS_RSU_AWARD_APPROVAL: () =>
    import('@apps/customer/domains/rsu-award-approval/RsuBulkApprovalProgress'),

  // ── PSU Award Approval ────────────────────────────────────────
  VIEW_PSU_AWARD_APPROVAL: () => import('@apps/customer/domains/psu-award-approval/ViewPsuAwardApproval'),
  VIEW_COMMENTS_PSU_AWARD_APPROVAL: () =>
    import('@apps/customer/domains/psu-award-approval/ViewCommentsPsuAwardApproval'),
  BULK_PROGRESS_PSU_AWARD_APPROVAL: () =>
    import('@apps/customer/domains/psu-award-approval/PsuBulkApprovalProgress'),

  // ── Exercise ──────────────────────────────────────────────────
  VIEW_DETAILS_EXERCISE: () => import('@apps/customer/domains/exercise/ViewExercise'),
  VIEW_CLOSING_PRICE_EXERCISE: () => import('@apps/customer/domains/exercise/HistoricalStockPrice'),

  // ── Release ───────────────────────────────────────────────────
  RENAME_RELEASE: () => import('@apps/customer/domains/release/RenameReleaseModel'),
  VIEW_DETAILS_RELEASE_INCLUDED: () =>
    import('@apps/customer/domains/release/IncludeItemModel'),
  VIEW_CLOSING_PRICE_RELEASE_INCLUDED: () =>
    import('@apps/customer/domains/release/HistoricalStockPrice'),
  VIEW_DETAILS_RELEASE_DETAILS: () =>
    import('@apps/customer/domains/release/TransactionDetailsModal'),

  // ── Performance Rules ─────────────────────────────────────────
  ADD_PERFORMANCE_RULE: () => import('@apps/customer/domains/performance-rule/PerformanceRulesModal'),
  VIEW_PERFORMANCE_RULE: () => import('@apps/customer/domains/performance-rule/PerformanceRulesModal'),
  EDIT_PERFORMANCE_RULE: () => import('@apps/customer/domains/performance-rule/PerformanceRulesModal'),
  VIEW_COMMENTS_PERFORMANCE_RULE: () =>
    import('@apps/customer/domains/performance-rule/ViewCommentsPerformanceRule'),

  // ── Performance Rule Approval ─────────────────────────────────
  VIEW_PERFORMANCE_RULE_APPROVAL: () =>
    import('@apps/customer/domains/performance-rule-approval/PerformanceRulesApprovalModal'),
  VIEW_COMMENTS_PERFORMANCE_RULE_APPROVAL: () =>
    import('@apps/customer/domains/performance-rule-approval/ViewCommentsPerformanceRuleApproval'),

  // ── Share Pool ────────────────────────────────────────────────
  // ADD/VIEW/EDIT_SHARE_POOL → OPEN_COMPOSE_MODAL → BUILD_SHARE_POOL_FORM

  // ── Transfer & Allocate ───────────────────────────────────────
  RENAME_TRANSFER_ALLOCATE: () =>
    import('@apps/customer/domains/transfer-and-allocate/RenameTransferAllocateModel'),
  VIEW_DETAILS_TRANSFER_ALLOCATE_ITEM: () =>
    import('@apps/customer/domains/transfer-and-allocate/IncludedModal'),
  VIEW_CLOSING_PRICE_TRANSFER_ALLOCATE: () =>
    import('@apps/customer/domains/transfer-and-allocate/HistoricalStockPrice'),

  // ── Cash Transfer ─────────────────────────────────────────────
  VIEW_CASH_TRANSFER: () => import('@apps/customer/domains/cash-transfer/ViewCashTransferModal'),

  // // ── Exercisability Tier Configuration ───────────────────────────
  // ADD_EXERCISABILITY_TIER_CONFIG: () =>
  //   import('@apps/customer/domains/exercisability-tier-config/CreateExercisabilityTierConfigModal'),
  // VIEW_EXERCISABILITY_TIER_CONFIG: () =>
  //   import('@apps/customer/domains/exercisability-tier-config/ViewExercisabilityTierConfig'),
  // EDIT_EXERCISABILITY_TIER_CONFIG: () =>
  //   import('@apps/customer/domains/exercisability-tier-config/EditExercisabilityTierConfigModal'),

  // // ── Exercisability Instructions ─────────────────────────────────
  // ADD_EXERCISABILITY_INSTRUCTIONS: () =>
  //   import('@apps/customer/domains/exercisability-instructions/CreateExercisabilityInstructionsModal'),
  // VIEW_EXERCISABILITY_INSTRUCTIONS: () =>
  //   import('@apps/customer/domains/exercisability-instructions/ViewExercisabilityInstructions'),
  // EDIT_EXERCISABILITY_INSTRUCTIONS: () =>
  //   import('@apps/customer/domains/exercisability-instructions/EditExercisabilityInstructionsModal'),

  // // ── SAR Awards ──────────────────────────────────────────────────
  // VIEW_SAR_AWARD: () => import('@apps/customer/domains/sar-awards/ViewSarAward'),
  // IMPORT_SAR_AWARD: () =>
  //   import('@apps/customer/domains/sar-awards/components/SarAwardImportModal'),

  // // ── SAR Award Approvals ─────────────────────────────────────────
  // VIEW_SAR_AWARD_APPROVAL_OVERVIEW: () =>
  //   import('@apps/customer/domains/sar-approvals/ViewSarAwardApprovalOverview'),
  // VIEW_SAR_AWARD_APPROVAL_VESTING: () =>
  //   import('@apps/customer/domains/sar-approvals/ViewSarAwardApprovalVesting'),
  // VIEW_SAR_AWARD_APPROVAL_DOCUMENTS: () =>
  //   import('@apps/customer/domains/sar-approvals/ViewSarAwardApprovalDocuments'),

  // // ── SAR My Holdings (Participant) ───────────────────────────────
  // VIEW_SAR_HOLDING: () => import('@apps/customer/domains/sar-my-holdings/ViewSarHoldingDetail'),

  // ── Transactions — Purchase Details ───────────────────────────
  VIEW_TRANSACTIONS_PURCHASEDETAILS: () =>
    import('@apps/customer/domains/transactions-purchase-details/TransactionDetailsModal'),

  // ── Trade Pre-Clearance ────────────────────────────────────────
  ADD_TRADE_PRE_CLEARANCE: () =>
    import('@apps/customer/domains/trade-pre-clearance/TradePreClearanceModal'),

  // ── Trade Pre-Clearance Approval ───────────────────────────────
  VIEW_TRADE_PRE_CLEARANCE_APPROVAL: () =>
    import('@apps/customer/domains/trade-pre-clearance-approval/PreClearanceRequestModal'),
  VIEW_REJECTED_NOTES_TRADE_PRE_CLEARANCE_APPROVAL: () =>
    import('@apps/customer/domains/trade-pre-clearance-approval/PreClearanceRequestRejectModal'),

  // ── Trading Blackout Config ────────────────────────────────────
  ADD_TRADING_BLACKOUT_CONFIG: () =>
    import('@apps/customer/domains/trading-blackout-config/TradingBlackoutConfigModal'),
  VIEW_TRADING_BLACKOUT_CONFIG: () =>
    import('@apps/customer/domains/trading-blackout-config/TradingBlackoutConfigModal'),
  EDIT_TRADING_BLACKOUT_CONFIG: () =>
    import('@apps/customer/domains/trading-blackout-config/TradingBlackoutConfigModal'),
  REPURPOSE_TRADING_BLACKOUT_CONFIG: () =>
    import('@apps/customer/domains/trading-blackout-config/TradingBlackoutConfigModal'),

  // ── Job Management Console ────────────────────────────────────
  VIEW_JOB_MANAGEMENT_CONSOLE: () =>
    import('@apps/customer/domains/job-management-console/TmcModel'),

  // ── Tax Certification Documents ────────────────────────────────
  VIEW_TAX_CERTIFICATE_DOCUMENT: () =>
    import('@apps/customer/domains/tax-certification-documents/TaxCertificatePdfRenderer'),

  // ── SAR Exercises ───────────────────────────────────────────────
  // VIEW_SAR_EXERCISE_DETAIL: () =>
  //   import('@apps/customer/domains/sar-exercises/ViewSarExerciseDetail'),
  // UPDATE_SAR_EXERCISE_FMV: () =>
  //   import('@apps/customer/domains/sar-exercises/UpdateSarExerciseFmvModal'),
  // UPDATE_SAR_EXERCISE_TAXES: () =>
  //   import('@apps/customer/domains/sar-exercises/UpdateSarExerciseTaxesModal'),
  // IMPORT_SAR_EXERCISE: () =>
  //   import('@apps/customer/domains/sar-exercises/ImportSarExerciseModal'),

  // ── System Users ────────────────────────────────────────────────
  ADD_SYSTEM_USER: () => import('@apps/customer/domains/system-users/UserModel'),
  VIEW_SYSTEM_USER: () => import('@apps/customer/domains/system-users/UserModel'),
  EDIT_SYSTEM_USER: () => import('@apps/customer/domains/system-users/UserModel'),
  DEACTIVATE_SYSTEM_USER: () => import('@apps/customer/domains/system-users/DeactivatePopup'),
  VIEW_COMMENTS_SYSTEM_USER: () =>
    import('@apps/customer/domains/system-users/ViewCommentsSystemUserModal'),

  // ── System User Approval ────────────────────────────────────────
  VIEW_SYSTEM_USER_APPROVAL: () => import('@apps/customer/domains/system-user-approval/ApprovalUserModel'),
  UPDATE_SYSTEM_USER_APPROVAL: () => import('@apps/customer/domains/system-user-approval/ApprovalUserModel'),
  VIEW_COMMENTS_SYSTEM_USER_APPROVAL: () =>
    import('@apps/customer/domains/system-users/ViewCommentsSystemUserModal'),

  // ── System Roles ────────────────────────────────────────────────
  ADD_SYSTEM_ROLE: () => import('@apps/customer/domains/system-roles/RoleModal'),
  VIEW_SYSTEM_ROLE: () => import('@apps/customer/domains/system-roles/RoleModal'),
  EDIT_SYSTEM_ROLE: () => import('@apps/customer/domains/system-roles/RoleModal'),
  COPY_SYSTEM_ROLE: () => import('@apps/customer/domains/system-roles/RoleModal'),
  FILTER_SYSTEM_ROLES: () => import('@apps/customer/domains/system-roles/FilterRolesModal'),
  BULK_EDIT_SYSTEM_ROLES: () => import('@apps/customer/domains/system-roles/ManageFeatureSetModal'),

  // ── Access profiles (unified API) ──
  ADD_ACCESS_PROFILE_ROLE: () =>
    import('@apps/customer/domains/access-profiles/AccessProfileFormModal'),
  VIEW_ACCESS_PROFILE_ROLE: () =>
    import('@apps/customer/domains/access-profiles/AccessProfileFormModal'),
  EDIT_ACCESS_PROFILE_ROLE: () =>
    import('@apps/customer/domains/access-profiles/AccessProfileFormModal'),
  COPY_ACCESS_PROFILE_ROLE: () =>
    import('@apps/customer/domains/access-profiles/AccessProfileFormModal'),
  FILTER_ACCESS_PROFILE_ROLE: () =>
    import('@apps/customer/domains/access-profiles/AccessProfileFilterModalWrapper'),
  BULK_EDIT_ACCESS_PROFILE_ROLE: () =>
    import('@apps/customer/domains/access-profiles/AccessProfileManageModal'),
  ADD_ACCESS_PROFILE_FEATURE_SET: () =>
    import('@apps/customer/domains/access-profiles/AccessProfileFormModal'),
  VIEW_ACCESS_PROFILE_FEATURE_SET: () =>
    import('@apps/customer/domains/access-profiles/AccessProfileFormModal'),
  EDIT_ACCESS_PROFILE_FEATURE_SET: () =>
    import('@apps/customer/domains/access-profiles/AccessProfileFormModal'),
  FILTER_ACCESS_PROFILE_FEATURE_SET: () =>
    import('@apps/customer/domains/access-profiles/AccessProfileFilterModalWrapper'),
  BULK_EDIT_ACCESS_PROFILE_FEATURE_SET: () =>
    import('@apps/customer/domains/access-profiles/AccessProfileManageModal'),

  // ── Tax Setup (V2 table — legacy modal shells, no JSON compose form) ──
  CREATE_TAX_SETUP: () => import('@apps/customer/domains/tax-setup/TaxSetupModal'),
  VIEW_TAX_SETUP: () => import('@apps/customer/domains/tax-setup/TaxSetupModal'),
  EDIT_TAX_SETUP: () => import('@apps/customer/domains/tax-setup/TaxSetupModal'),
  COPY_TAX_SETUP: () => import('@apps/customer/domains/tax-setup/TaxSetupModal'),
  OVERRIDE_TAX_SETUP: () => import('@apps/customer/domains/tax-setup/TaxOverrideModal'),
  VIEW_COMMENT_TAX_SETUP: () => import('@apps/customer/domains/tax-setup/ViewCommentTaxSetupModal'),

  // ── Tax Setup Approval ──────────────────────────────────────────
  VIEW_TAX_SETUP_APPROVAL: () => import('@apps/customer/domains/tax-setup-approval/TaxSetupApproverModal'),
  VIEW_COMMENT_TAX_SETUP_APPROVAL: () =>
    import('@apps/customer/domains/tax-setup-approval/ViewCommentTaxSetupApprovalModal'),

  // ── Commission ────────────────────────────────────────────────
  ADD_COMMISSION: () => import('@apps/customer/domains/commission/AddCommission'),
  VIEW_COMMISSION: () => import('@apps/customer/domains/commission/ViewCommission'),
  EDIT_COMMISSION: () => import('@apps/customer/domains/commission/EditCommission'),

  // ── Fee ───────────────────────────────────────────────────────
  ADD_FEE: () => import('@apps/customer/domains/fee/AddFee'),
  EDIT_FEE: () => import('@apps/customer/domains/fee/EditFee'),

  // ── Company SSO ───────────────────────────────────────────────
  ADD_COMPANY_SSO: () => import('@apps/customer/domains/company-sso/SsoConfigurationModal'),
  VIEW_COMPANY_SSO: () => import('@apps/customer/domains/company-sso/SsoConfigurationModal'),
  EDIT_COMPANY_SSO: () => import('@apps/customer/domains/company-sso/SsoConfigurationModal'),
  UPDATE_SIGNING_CERTIFICATE_COMPANY_SSO: () =>
    import('@apps/customer/domains/company-sso/UpdateSigningCertificateModal'),

  // ── Company Mapping ─────────────────────────────────────────────
  ADD_COMPANY_MAPPING: () => import('@apps/customer/domains/company-mapping/CompanyMapModal'),
  EDIT_COMPANY_MAPPING: () => import('@apps/customer/domains/company-mapping/CompanyMapModal'),

  // ── Feature Set ─────────────────────────────────────────────────
  ADD_FEATURE_SET: () => import('@apps/customer/domains/feature-set/FeatureSetModal'),
  VIEW_FEATURE_SET: () => import('@apps/customer/domains/feature-set/FeatureSetModal'),
  EDIT_FEATURE_SET: () => import('@apps/customer/domains/feature-set/FeatureSetModal'),
  BULK_EDIT_FEATURE_SET: () => import('@apps/customer/domains/feature-set/ManageFeatureSetModal'),
  FILTER_FEATURE_SET: () => import('@apps/customer/domains/feature-set/FilterFeatureSetModal'),

  // ── Sandbox ───────────────────────────────────────────────────
  LOAD_TO_MAIN_SANDBOX: () => import('@apps/customer/domains/sandbox/LoadToMainSandbox'),
  LOAD_TO_MAIN_ROW_SANDBOX: () => import('@apps/customer/domains/sandbox/LoadToMainSandbox'),
  ADD_SANDBOX: () => import('@apps/customer/domains/sandbox/AddSandbox'),
  EDIT_SANDBOX: () => import('@apps/customer/domains/sandbox/EditSandbox'),
  SHARE_SANDBOX: () => import('@apps/customer/domains/sandbox/ShareSandbox'),
  CHANGE_STATUS_SANDBOX: () => import('@apps/customer/domains/sandbox/ChangeStatusSandbox'),
};

const componentCache = new Map<string, React.ComponentType<any>>();

// Clear the modal component cache on HMR so lazily-loaded modal files are
// re-fetched after edits without requiring a hard browser refresh.
if (import.meta.hot) {
  import.meta.hot.on('vite:beforeUpdate', () => {
    componentCache.clear();
  });
}

let intentCounter = 0;

type StackAction =
  | { type: 'PUSH'; entry: ModalEntry }
  | { type: 'OPEN'; key: string }
  | { type: 'CLOSE'; key: string }
  | { type: 'CLOSE_BY_ACTION'; actionId: string }
  | { type: 'CLOSE_TOP' }
  | { type: 'CLOSE_ALL' }
  | { type: 'REMOVE'; key: string };

function stackReducer(state: ModalEntry[], action: StackAction): ModalEntry[] {
  switch (action.type) {
    case 'PUSH': {
      const hasPendingOrOpen = state.some(
        (e) => e.actionId === action.entry.actionId && !e.closing,
      );
      if (hasPendingOrOpen) return state;
      return [...state, action.entry];
    }
    case 'OPEN':
      return state.map((e) => e.key === action.key ? { ...e, isOpen: true } : e);
    case 'CLOSE':
      return state.map((e) => e.key === action.key ? { ...e, isOpen: false, closing: true } : e);
    case 'CLOSE_BY_ACTION':
      return state.map((e) =>
        e.actionId === action.actionId && !e.closing ? { ...e, isOpen: false, closing: true } : e,
      );
    case 'CLOSE_TOP': {
      const topIdx = state.findLastIndex((e) => e.isOpen && !e.closing);
      if (topIdx === -1) return state;
      return state.map((e, i) => i === topIdx ? { ...e, isOpen: false, closing: true } : e);
    }
    case 'CLOSE_ALL':
      return state.map((e) => e.closing ? e : { ...e, isOpen: false, closing: true });
    case 'REMOVE':
      return state.filter((e) => e.key !== action.key);
    default: {
      const _exhaustive: never = action;
      return state;
    }
  }
}

/**
 * Mediator component — subscribes to ActionRegistry intents and lazy-loads
 * the appropriate modal. Mounted once in CustomerAppShell.
 *
 * Supports stacked modals: a new intent pushes onto the stack, rendering
 * on top of existing modals. Closing pops the top modal off the stack.
 */
export function ActionIntentHandler() {
  const [stack, dispatch] = useReducer(stackReducer, []);
  const lastIntentRef = useRef<{ actionId: string; ts: number } | null>(null);

  const handleIntent = useCallback((intent: ActionIntent) => {
    if (intent.type === 'OPEN_COMPOSE_POPOVER') {
      return;
    }

    intentCounter += 1;
    const key = `${intent.actionId}-${intentCounter}`;

    if (intent.type === 'OPEN_COMPOSE_MODAL') {
      if (intent.loader) {
        void intent.loader().then((mod) => {
          const Component = componentCache.get(intent.actionId) ?? mod.default;
          if (!componentCache.has(intent.actionId)) {
            componentCache.set(intent.actionId, mod.default);
          }
          dispatch({
            type: 'PUSH',
            entry: {
              Component,
              context: intent.context,
              key,
              actionId: intent.actionId,
              isOpen: false,
              closing: false,
            },
          });
          requestAnimationFrame(() => { dispatch({ type: 'OPEN', key }); });
        });
      } else {
        dispatch({
          type: 'PUSH',
          entry: {
            Component: ComposeModal,
            context: intent.context,
            key,
            actionId: intent.actionId,
            isOpen: false,
            closing: false,
            isComposeModal: true,
          },
        });
        requestAnimationFrame(() => { dispatch({ type: 'OPEN', key }); });
      }
      return;
    }

    if (intent.type !== 'OPEN_MODAL') return;

    const now = Date.now();
    const last = lastIntentRef.current;
    if (last && last.actionId === intent.actionId && now - last.ts < 500) {
      return;
    }
    lastIntentRef.current = { actionId: intent.actionId, ts: now };

    const loader = modalLoaders[intent.actionId];
    if (!loader) {
      console.warn(`[ActionIntentHandler] No modal loader for "${intent.actionId}"`);
      return;
    }

    void loader().then((mod) => {
      const Component = componentCache.get(intent.actionId) ?? mod.default;
      if (!componentCache.has(intent.actionId)) {
        componentCache.set(intent.actionId, mod.default);
      }

      dispatch({
        type: 'PUSH',
        entry: {
          Component,
          context: intent.context,
          payload: intent.type === 'OPEN_MODAL' ? intent.payload : undefined,
          key,
          actionId: intent.actionId,
          isOpen: false,
          closing: false,
        },
      });

      requestAnimationFrame(() => {
        dispatch({ type: 'OPEN', key });
      });
    });
  }, []);

  useEffect(() => {
    return ActionRegistry.onIntent(handleIntent);
  }, [handleIntent]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail != null) handleIntent(detail as ActionIntent);
    };
    window.addEventListener('ui:action-intent', handler);
    return () => window.removeEventListener('ui:action-intent', handler);
  }, [handleIntent]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<ModalCloseDetail | undefined>).detail;
      if (detail?.actionId != null) {
        dispatch({ type: 'CLOSE_BY_ACTION', actionId: detail.actionId });
      } else if (detail?.top) {
        dispatch({ type: 'CLOSE_TOP' });
      } else {
        dispatch({ type: 'CLOSE_ALL' });
      }
    };
    window.addEventListener('ui:close-modal', handler);
    window.addEventListener('ui:close-all-modals', handler);
    return () => {
      window.removeEventListener('ui:close-modal', handler);
      window.removeEventListener('ui:close-all-modals', handler);
    };
  }, []);

  if (stack.length === 0) return null;

  return (
    <>
      {stack.map((entry) => (
        <entry.Component
          key={entry.key}
          {...(entry.isComposeModal
            ? { actionId: entry.actionId, context: entry.context, composeInstanceKey: entry.key }
            : entry.context != null ? { context: entry.context } : entry.payload
          )}
          isOpen={entry.isOpen}
          onClose={() => dispatch({ type: 'CLOSE', key: entry.key })}
          onExitComplete={() => dispatch({ type: 'REMOVE', key: entry.key })}
        />
      ))}
    </>
  );
}
